import { useCallback, useEffect, useRef, useState } from "react";

import { useApi } from "@platform/hooks/useApi";

import type {
  ManagedResourceUI,
  ResourceMetricsUI,
  ResourceActivityEntryUI,
  ResourceCredentialsUI,
} from "../types";

interface UseResourceReturn {
  resource: ManagedResourceUI | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/** Fetch a single managed resource by alias. */
export function useResource(
  resourceAlias: string | undefined,
): UseResourceReturn {
  const api = useApi();
  const [resource, setResource] = useState<ManagedResourceUI | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!resourceAlias) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<ManagedResourceUI>(
        `/api/v1/resources/${encodeURIComponent(resourceAlias)}`,
        {
          errorMessage: `Failed to load resource: ${resourceAlias}`,
          showLoading: false,
        },
      );
      setResource(result);
    } catch {
      setError("Failed to load resource.");
    } finally {
      setLoading(false);
    }
    // `api` excluded — see useProjectResources note. api.get is stable.
  }, [resourceAlias]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { resource, loading, error, reload };
}

interface UseResourceMetricsReturn {
  metrics: ResourceMetricsUI | null;
  loading: boolean;
  error: string | null;
}

/** Fetch resource metrics once on mount (no polling). */
export function useResourceMetrics(
  resourceAlias: string | undefined,
): UseResourceMetricsReturn {
  const api = useApi();
  const [metrics, setMetrics] = useState<ResourceMetricsUI | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!resourceAlias) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<ResourceMetricsUI>(
        `/api/v1/resources/${encodeURIComponent(resourceAlias)}/metrics`,
        {
          errorMessage: `Failed to load metrics for resource: ${resourceAlias}`,
          showLoading: false,
        },
      );
      setMetrics(result);
    } catch {
      setError("Failed to load metrics.");
    } finally {
      setLoading(false);
    }
    // `api` excluded — see useProjectResources note. api.get is stable.
  }, [resourceAlias]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { metrics, loading, error };
}

interface UseResourceActivityReturn {
  activity: ResourceActivityEntryUI[];
  total: number;
  loading: boolean;
  error: string | null;
  reload: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

/** Fetch resource activity log with pagination. */
export function useResourceActivity(
  resourceAlias: string | undefined,
  pageSize = 10,
): UseResourceActivityReturn {
  const api = useApi();
  const [activity, setActivity] = useState<ResourceActivityEntryUI[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const offsetRef = useRef(0);

  const loadPage = useCallback(
    async (offset: number, append: boolean) => {
      if (!resourceAlias) return;
      setLoading(true);
      setError(null);
      try {
        const result = await api.get<{
          entries: ResourceActivityEntryUI[];
          total: number;
        }>(
          `/api/v1/resources/${encodeURIComponent(resourceAlias)}/activity?limit=${pageSize}&offset=${offset}`,
          {
            errorMessage: `Failed to load activity for resource: ${resourceAlias}`,
            showLoading: false,
          },
        );
        if (!result) return;
        setTotal(result.total);
        if (append) {
          setActivity((prev) => [...prev, ...result.entries]);
        } else {
          setActivity(result.entries);
        }
      } catch {
        setError("Failed to load activity.");
      } finally {
        setLoading(false);
      }
    },
    [resourceAlias, pageSize],
  );

  useEffect(() => {
    offsetRef.current = 0;
    void loadPage(0, false);
  }, [loadPage]);

  const loadMore = useCallback(() => {
    offsetRef.current += pageSize;
    void loadPage(offsetRef.current, true);
  }, [loadPage, pageSize]);

  const hasMore = activity.length < total;

  const reload = useCallback(() => {
    offsetRef.current = 0;
    void loadPage(0, false);
  }, [loadPage]);

  return { activity, total, loading, error, reload, loadMore, hasMore };
}

interface UseResourceCredentialsReturn {
  credentials: ResourceCredentialsUI | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Fetch the credentials for a managed resource.
 * Backed by GET /api/v1/resources/:alias/credentials with the project scope
 * supplied as a required query parameter.
 * Credentials are fetched once on mount (no polling — they don't change).
 */
export function useResourceCredentials(
  resourceAlias: string | undefined,
  projectAlias: string | undefined,
): UseResourceCredentialsReturn {
  const api = useApi();
  const [credentials, setCredentials] = useState<ResourceCredentialsUI | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!resourceAlias || !projectAlias) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<ResourceCredentialsUI>(
        `/api/v1/resources/${encodeURIComponent(resourceAlias)}/credentials?projectAlias=${encodeURIComponent(projectAlias)}`,
        {
          errorMessage: `Failed to load credentials for resource: ${resourceAlias}`,
          showLoading: false,
          showErrorNotification: false,
        },
      );
      setCredentials(result);
    } catch {
      setError("Failed to load credentials.");
      setCredentials(null);
    } finally {
      setLoading(false);
    }
  }, [projectAlias, resourceAlias]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { credentials, loading, error, reload };
}
